import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Copy,
  GitBranch,
  GripVertical,
  Trash2
} from "lucide-react";
import type { FC } from "react";

import type { OutgoingIntakeFormNode } from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import { showIfChipLabel } from "@/features/foreignNationalOutgoingIntakes/conditional";
import { stepLabel, type Step } from "@/features/requestsIde/editor/stepModel";
import { cn } from "@/lib/utils";

import { typeOf } from "../model";
import { StepFields } from "./QuestionFields";

export type QuestionRowProps = {
  step: Step;
  schema: OutgoingIntakeFormNode[];
  who: string;
  open: boolean;
  readOnly: boolean;
  /** The first pre-send error on this step, shown under it. */
  issue?: string | null;
  /** Changed by Casey since the last look: carries an accent bar. */
  fresh?: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<OutgoingIntakeFormNode>) => void;
  onDuplicate: () => void;
  onMove: (delta: -1 | 1) => void;
  onDelete: () => void;
};

const metaOf = (node: OutgoingIntakeFormNode): string[] => {
  const t = typeOf(node);
  if (node.node_type === "repeatable_group") {
    const n = node.children?.length ?? 0;
    return [
      t.label,
      n ? `Asks ${n} thing${n === 1 ? "" : "s"} per entry` : "Nothing asked yet"
    ];
  }
  return [t.label, node.required ? "Required" : "Optional"];
};

/**
 * One step in the questions card. Collapsed it reads like the client's form
 * (number, type, wording, rules); open it edits in place.
 */
export const QuestionRow: FC<QuestionRowProps> = ({
  step,
  schema,
  who,
  open,
  readOnly,
  issue,
  fresh,
  isFirst,
  isLast,
  onToggle,
  onChange,
  onDuplicate,
  onMove,
  onDelete
}) => {
  const { node } = step;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: node.node_id, disabled: readOnly || open });
  const t = typeOf(node);
  const rule = showIfChipLabel(node, schema);

  return (
    <div
      ref={setNodeRef}
      data-builder-row={node.node_id}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "group/row relative border-t border-line-1",
        open && "bg-primary/5",
        isDragging && "z-10 bg-elevated opacity-80"
      )}
    >
      {open || fresh ? (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-0.5 bg-primary"
        />
      ) : null}
      <div className="flex items-start gap-2.5 py-2.5 pr-3 pl-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-start gap-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span className="w-5 shrink-0 pt-1 text-right text-sm text-fg-4 tabular-nums">
            {step.number}
          </span>
          <IconTile icon={t.icon} />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
            <span
              className={cn(
                "text-base text-fg",
                open ? "font-medium" : "truncate"
              )}
            >
              {stepLabel(node)}
            </span>
            <span className="flex flex-wrap items-center gap-1.5 text-sm text-fg-3">
              {metaOf(node).map((m, i) => (
                <span key={m} className="inline-flex items-center gap-1.5">
                  {i ? <span className="text-fg-4">·</span> : null}
                  {m}
                </span>
              ))}
            </span>
            {rule ? (
              <span className="inline-flex items-center gap-1 text-sm text-info">
                <GitBranch className="size-3" /> {rule}
              </span>
            ) : null}
            {issue && !readOnly ? (
              <span className="inline-flex items-center gap-1 text-sm text-danger">
                <AlertCircle className="size-3" /> {issue}
              </span>
            ) : null}
          </span>
        </button>
        {open || readOnly ? null : (
          <span
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            aria-label={`Drag ${stepLabel(node)}`}
            className="mt-1 cursor-grab rounded-sm text-fg-4 opacity-0 outline-none group-hover/row:opacity-100 hover:text-fg focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/40 active:cursor-grabbing"
          >
            <GripVertical className="size-3.5" />
          </span>
        )}
      </div>
      {open ? (
        <div className="flex flex-col gap-3 pr-4 pb-3.5 pl-15.5">
          <StepFields
            node={node}
            schema={schema}
            who={who}
            readOnly={readOnly}
            onChange={onChange}
          />
          <div className="flex items-center gap-0.5 border-t border-line-1 pt-2.5">
            {readOnly ? null : (
              <>
                <Button variant="ghost" size="xs" onClick={onDuplicate}>
                  <Copy className="size-3" /> Duplicate
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Move Up"
                  disabled={isFirst}
                  onClick={() => onMove(-1)}
                >
                  <ArrowUp className="size-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Move Down"
                  disabled={isLast}
                  onClick={() => onMove(1)}
                >
                  <ArrowDown className="size-3" />
                </Button>
                <Button variant="danger" size="xs" onClick={onDelete}>
                  <Trash2 className="size-3" /> Delete
                </Button>
              </>
            )}
            <span className="flex-1" />
            <Button size="xs" onClick={onToggle}>
              Done
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
